/**
 * ChatPanelHeader — top bar of the slide-over (and reused on the fullpage
 * left column). Title + New-conversation + Feedback + History + Pacing +
 * Expand-to-fullpage + Close.
 *
 * Buttons are presentational; their handlers come from the parent so the
 * fullpage variant can hide Close and the slide-over variant can hide
 * Expand-back-to-slide-over (those are different gestures).
 *
 * TWO ROWS WHEN NARROW (2026-09-16): the allowance chip (IonRemainingChip)
 * sits inline with the buttons only on `full`. Everywhere else the header is
 * the width of the slide-over, and a single row could not hold the BATEMO ION
 * lockup, the SNEAK PREVIEW badge, the chip and six buttons — the chip landed
 * on top of the wordmark and the badge. Below `full` the chip therefore gets
 * its own row under the brand, where its bar has room to be read. The row
 * collapses on its own when the chip renders null (`:empty` in ion.css), so a
 * FREE/unlimited profile pays no extra height for it.
 */

import { IconMaximize, IconSliders, IconX } from '../shared/Icons.jsx'
import { IonLockup } from './IonLockup.jsx'
import { IonRemainingChip } from './IonRemainingChip.jsx'
import { t } from '../../i18n/index.js'
import { ION_SNEAK_PREVIEW } from '../../config/ionConfig.js'

/**
 * @param {object} props
 * @param {string} [props.title='Ion']
 * @param {string} [props.subtitle] — small caption under the title (e.g. model)
 * @param {() => void} [props.onNewConversation] — show button if provided
 * @param {() => void} [props.onExpand] — show button if provided
 * @param {() => void} [props.onCollapse] — show button if provided (fullpage → slide-over)
 * @param {() => void} [props.onHistory] — show button if provided
 * @param {() => void} [props.onPacing] — show the pacing button if provided (PRO/batemo)
 * @param {() => void} [props.onFeedback] — show admin feedback button if provided
 * @param {() => void} [props.onMinimize] — show minimize button if provided
 * @param {() => void} [props.onClose] — show button if provided
 * @param {'rail'|'docked'|'full'} [props.surface] — which geometry this header
 *   is drawn in. Only `full` has the width to keep the allowance chip inline
 *   with the buttons; everything else (including an unspecified surface, which
 *   is the narrow case) puts it on its own row under the brand.
 */
export function ChatPanelHeader({
  title,
  subtitle,
  surface,
  onNewConversation,
  onExpand,
  onCollapse,
  onHistory,
  onPacing,
  onFeedback,
  onMinimize,
  onClose,
}) {
  const usageBelow = surface !== 'full'

  return (
    <header class="ion-header">
      <div class="ion-header__bar">
        <div class="ion-header__brand">
          <div class="ion-header__titles">
            {/* "ION", not the full BATEMO ION: the header is the one place the
                wordmark competes with the SNEAK PREVIEW badge and the controls
                for a slide-over's width, and the app around it already says
                whose assistant this is. The other lockups (home, empty state,
                nav) keep the full name. */}
            <IonLockup size={16} animated short className="ion-header__lockup" />
            {subtitle && <span class="ion-header__subtitle">{subtitle}</span>}
          </div>
          {ION_SNEAK_PREVIEW && (
            <span class="ion-header__preview-badge">
              {t('ion.header.sneak_preview')}
            </span>
          )}
        </div>
        <nav class="ion-header__actions" aria-label={t('ion.header.title')}>
          {/* Insights-token allowance used against the cap — self-guards to null
              for unlimited / no-budget profiles. On `full` it sits with the
              controls because it answers "can I keep going?" right where the
              user acts; narrower, it moves to its own row below (see the row
              after this nav) rather than crowding the wordmark. */}
          {!usageBelow && <IonRemainingChip />}
          {onNewConversation && (
            <button
              type="button"
              class="ion-header__btn"
              onClick={onNewConversation}
              aria-label={t('ion.header.new_conversation')}
              title={t('ion.header.new_conversation')}
            >
              <NewConversationIcon />
            </button>
          )}
          {onFeedback && (
            <button
              type="button"
              class="ion-header__btn"
              onClick={onFeedback}
              aria-label={t('ion.header.feedback')}
              title={t('ion.header.feedback')}
            >
              <FeedbackIcon />
            </button>
          )}
          {onHistory && (
            <button
              type="button"
              class="ion-header__btn"
              onClick={onHistory}
              aria-label={t('ion.header.history')}
            >
              <HistoryIcon />
            </button>
          )}
          {/* Pacing (PRO/batemo only — the caller decides by passing the
              handler at all). It lived as an underlined link inside the
              usage-meter tile, which forced that tile to render even with no
              budget window near its cap, leaving a full-width strip under the
              header holding one sentence and one link. A control belongs with
              the other controls. */}
          {onPacing && (
            <button
              type="button"
              class="ion-header__btn"
              onClick={onPacing}
              aria-label={t('ion.header.pacing')}
              title={t('ion.header.pacing')}
            >
              <IconSliders size={18} />
            </button>
          )}
          {onExpand && (
            <button
              type="button"
              class="ion-header__btn"
              onClick={onExpand}
              aria-label={t('ion.header.expand')}
            >
              <IconMaximize size={18} />
            </button>
          )}
          {onCollapse && (
            <button
              type="button"
              class="ion-header__btn"
              onClick={onCollapse}
              aria-label={t('ion.header.collapse')}
            >
              <CollapseIcon />
            </button>
          )}
          {onMinimize && (
            <button
              type="button"
              class="ion-header__btn"
              onClick={onMinimize}
              aria-label={t('ion.header.minimize')}
              title={t('ion.header.minimize')}
            >
              <MinimizeIcon />
            </button>
          )}
          {onClose && (
            <button
              type="button"
              class="ion-header__btn"
              onClick={onClose}
              aria-label={t('ion.header.close')}
            >
              <IconX size={18} />
            </button>
          )}
        </nav>
      </div>
      {/* Own row below the brand on every surface but `full`: a 44px bar plus
          its n/limit figure does not fit beside the wordmark, the SNEAK PREVIEW
          badge and six buttons at slide-over width. Collapses to nothing when
          IonRemainingChip renders null — see `.ion-header__usage:empty`. */}
      {usageBelow && (
        <div class="ion-header__usage">
          <IonRemainingChip />
        </div>
      )}
    </header>
  )
}

// Local icons not yet in shared/Icons.jsx — keep inline to avoid editing the
// shared file from a feature-scoped commit. Move to shared/ if reused.

function HistoryIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v5h5" />
      <path d="M12 7v5l3 2" />
    </svg>
  )
}

function NewConversationIcon() {
  // Pencil-square / message-edit composite — communicates "start fresh"
  // without colliding with History's clock-arrow.
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h9" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L13 14l-4 1 1-4 8.5-8.5z" />
    </svg>
  )
}

function FeedbackIcon() {
  // Flag — "report / leave feedback". Deliberately a distinct silhouette
  // from the pencil-square New-conversation icon next to it.
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <line x1="4" y1="22" x2="4" y2="15" />
    </svg>
  )
}

function CollapseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M9 4H5a1 1 0 0 0-1 1v4" />
      <path d="M15 4h4a1 1 0 0 1 1 1v4" />
      <path d="M9 20H5a1 1 0 0 1-1-1v-4" />
      <path d="M15 20h4a1 1 0 0 0 1-1v-4" />
    </svg>
  )
}

function MinimizeIcon() {
  // Chevron-down — "tuck the chat down into the launcher". Distinct from the
  // collapse-inward (fullpage→slide-over) and the X (close).
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
}
